/**
 * The one place the diary is asked what mood she is in.
 *
 * Headless: it renders nothing and is mounted once, high in the shell. It
 * watches the same Dexie tables everything else does, so a meal logged thirty
 * seconds ago has already changed her — and it publishes the answer to a
 * store rather than through props, because the things that need it are not in
 * one subtree. The greeting is in Today, the toast layer is in a portal
 * attached to the body, the sound engine is not a component at all, and the
 * palette is a CSS attribute on the root element.
 *
 * Two clocks matter here and they are not the same. Dexie's live query covers
 * everything that changes because the diary changed. The interval covers the
 * one thing that changes because time passed: at midnight the date rolls, the
 * silence gets a day longer, Wednesday starts or ends, and a big day begins.
 * An app left open on a phone overnight — which is most nights — would
 * otherwise still be greeting yesterday.
 */

import { deviceTimeZone, herLifeFor, isSagaFinale, localDateString } from '@gramas/shared'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useRef, useState } from 'react'
import { fireAmbient } from '../glitime/ambient.js'
import { syncCooledStamp } from '../glitime/interaction.js'
import { glitimeMetAt } from '../glitime/store.js'
import { herLifeFactsFor, moodBasisFor } from '../sass/facts.js'
import { currentProfile } from '../screens/useProfile.js'
import { playCue } from '../sound/engine.js'
import { bigDayFor, moodFrom, setPresence, takeBigDayCue } from './store.js'

/**
 * How often the wall clock is checked. A minute is far more often than
 * anything here changes, and costs one date comparison — the query below only
 * re-runs when the answer actually differs.
 */
const CLOCK_TICK_MS = 60_000

/** The kettle's evening shift, per PERSONA.md's "Netikėtos akimirkos". */
const KETTLE_HOUR = 21

export function PresenceController() {
  const [today, setToday] = useState(() => localDateString(new Date(), deviceTimeZone()))
  const profile = currentProfile()
  const userId = profile?.id ?? 'anon'

  // The date+hour this tick already tried the kettle for, so the same minute
  // firing again off a visibilitychange (or the interval simply ticking
  // through the rest of the hour) does not attempt it sixty times over —
  // `fireAmbient` is already idempotent once the slot is spent, this just
  // keeps it from being asked.
  const kettleTriedRef = useRef<string | null>(null)

  // Midnight, a phone unlocked after being asleep, and a tab brought back to
  // the front are the three ways the date can change without anything being
  // written. All three land here — and, riding the same tick, the one minute
  // a day the kettle's ambient line is allowed to fire.
  useEffect(() => {
    const check = () => {
      const now = localDateString(new Date(), deviceTimeZone())
      setToday((current) => (current === now ? current : now))

      const hour = new Date().getHours()
      if (hour === KETTLE_HOUR && !document.hidden) {
        const key = `${now}-${hour}`
        if (kettleTriedRef.current !== key) {
          kettleTriedRef.current = key
          fireAmbient('ambient_kettle', hour)
        }
      }
    }
    const handle = setInterval(check, CLOCK_TICK_MS)
    document.addEventListener('visibilitychange', check)
    return () => {
      clearInterval(handle)
      document.removeEventListener('visibilitychange', check)
    }
  }, [])

  const presence = useLiveQuery(async () => {
    const [basis, herFacts, metAt] = await Promise.all([
      moodBasisFor(today),
      herLifeFactsFor(today),
      glitimeMetAt(),
    ])
    const herLife = herLifeFor(userId, today, herFacts)
    return {
      mood: moodFrom(basis, basis.facts.daysSinceLastLog),
      daysSinceLastLog: basis.facts.daysSinceLastLog,
      herLife,
      sagaFinale: herLife != null && isSagaFinale(herLife) ? herLife : null,
      bigDay: bigDayFor(new Date(), { metAt, birthDate: profile?.birthDate ?? null }),
      date: today,
    }
  }, [today, userId, profile?.birthDate])

  useEffect(() => {
    if (presence == null) return
    setPresence({ ...presence, now: new Date() })
    // The cooled shade rides on its own attribute, re-asserted here so a
    // reload keeps it and the midnight rollover clears it.
    syncCooledStamp()
  }, [presence])

  // The big-day chirp. `presence` recomputes on every diary write, not just
  // once a day, so the once-per-day guard lives in Dexie meta (`takeBigDayCue`)
  // rather than in a ref here — a ref would forget by the next reload.
  useEffect(() => {
    if (presence?.bigDay == null) return
    void takeBigDayCue(presence.date, userId).then((should) => {
      if (should) playCue('bigday')
    })
  }, [presence?.bigDay, presence?.date, userId])

  return null
}
